let serverTime: Date | undefined;
let receivedTime: number | undefined;
const originalDateConstructor = Date;

/**
 * @description Parse ISO 8601 dates, according to the specification given for Date.parse.  This works around an unfixed bug with timezone parsing in Safari 12+
 * (https://bugs.webkit.org/show_bug.cgi?id=196894) and a fixed bug in Chrome 56 and below (https://bugs.chromium.org/p/chromium/issues/detail?id=391730).
 * @param {string} dateStr An ISO 8601 date to be parsed.
 * @returns {Number} The date, as a number of milliseconds since Jan 01, 1970.
 */
export let parseISODate = function (dateStr: string)
{
    // Date string format https://tc39.es/ecma262/#sec-date-time-string-format
    // Parsing and default values: https://tc39.es/ecma262/#sec-date.parse
    // V8's implementation: https://cs.chromium.org/chromium/src/v8/src/date/dateparser-inl.h?dr=CSs&g=0&l=24
    // Spidermonkey's implementation: https://searchfox.org/mozilla-central/source/js/src/jsdate.cpp#837

    /*
    function optional(re)
    {
        return "(?:" + re + ")?";
    }
    function group(re)
    {
        return "(" + re + ")";
    }
    var parseYear = group("\\d{4}|[-+]\\d{6}"); // 4 year digits, or -+ and 6 year digits.  (year: group 1)
    var dayOrMonth = "-" + group("\\d{2}"); // "-" and 2 digits.
    var parseMonthAndDay = optional(dayOrMonth + optional(dayOrMonth)); // month: group 2, day: group 3

    var parseHoursAndMinutes = "(\\d{2}):(\\d{2})"; // 2 hour digits, a colon, and 2 minute digits.

    // A colon, then 2 digits and optionally, a decimal place and 1 or more decimal digits.
    // In the specification, there must be exactly 3 decimal digits if any, but all implementations follow this looser rule.
    // We have to parse seconds separately from milliseconds due to float precision issues.
    var parseSeconds = ":" + group("\\d{2}") + optional("\\." + group("\\d+"));

    // A "Z", or "+" or "-" followed by 2 hour digits, a colon, and 2 minute digits.
    // The specification requires a colon, but all implementations except for IE allow leaving it out.
    var parseTimeZone = group("Z|[-+]\\d{2}:\\d{2}");
    // Time zone is optional, but as it's already in a capturing group, we just add a "?" instead of using optional() to wrap it in a non-capturing group
    var parseTime = optional("T" + parseHoursAndMinutes + parseSeconds + parseTimeZone + "?");
    // Wrap all the parts of the parser in ^ and $. ^ matches the start of the string, and $ matches the end, so this ensures there will be no leftover
    // leading or trailing characters.
    var iso8601 = new RegExp("^" + parseYear + parseMonthAndDay + parseTime + "$");
    */
    // This magic regex is produced by the above code
    const iso8601 = /^(\d{4}|[-+]\d{6})(?:-(\d{2})(?:-(\d{2}))?)?(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[-+]\d{2}:\d{2})?)?$/;
    const match = iso8601.exec(dateStr);
    if (match === null)
    {
        return NaN;
    }
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2] || "1", 10);
    const day = parseInt(match[3] || "1", 10);
    const hasTime = match[4] !== undefined;
    const hours = parseInt(match[4] || "0", 10);
    const minutes = parseInt(match[5] || "0", 10);
    const seconds = parseInt(match[6] || "0", 10);
    // Safari returns fractional milliseconds, but all other browsers simply drop trailing digits (rather than rounding)
    const milliseconds = parseInt((match[7] || "").padRight(3, "0").slice(0, 3), 10);
    let timezone = match[8];
    // months are indexed from 0 in Javascript
    const jsMonth = month - 1;

    // Out-of-range values return NaN
    if (!isValidYearMonthDay(year, month, day))
    {
        return NaN;
    }
    // Check that times are in-range.  24:00:00 is allowed and corresponds to midnight on the following day.  Negative values are impossible because they would have failed parsing.
    if (hours > 24 || minutes >= 60 || seconds >= 60 || (hours === 24 && (minutes > 0 || seconds > 0)))
    {
        return NaN;
    }
    // When no time is specified, the date is assumed to be in UTC.  When a time is specified but no timezone, the date is assumed to be in local time.
    // Safari gets this second part wrong, and uses UTC time instead.
    // It would be nice if we could tack on "Z" or the local timezone and use Date.parse instead of fully parsing it ourselves.  But the parsed date
    // should have the timezone *as of that date* - that is, 2001-01-01 is in CST, whereas 2001-07-01 is in CDT.  So using "new Date().getTimezoneOffset()" doesn't work.
    // We can't tack on "Z" either because we might miss the threshold for a timezone change: 2019-03-10T03:00Z is in CST whereas 2019-03-10T03:00 is in CDT.
    if (!hasTime)
    {
        timezone = "Z";
    }
    if (timezone === undefined)
    {
        return newDate(year, jsMonth, day, hours, minutes, seconds, milliseconds).getTime();
    }
    const utcDate = new Date(Date.UTC(year, jsMonth, day, hours, minutes, seconds, milliseconds));
    if (year <= 99 && year >= 0)
    {
        utcDate.setUTCFullYear(utcDate.getUTCFullYear() - 1900);
    }
    const utcTime = utcDate.getTime();
    if (timezone === "Z")
    {
        return utcTime;
    }
    const tzParts = timezone.slice(1).split(":", 2);
    const tzHours = parseInt(tzParts[0], 10);
    const tzMinutes = parseInt(tzParts[1], 10);
    if (tzHours >= 24 || tzMinutes >= 60)
    {
        return NaN;
    }
    // We want to subtract the timezone offset (going from UTC+X to UTC+0)
    const direction = timezone[0] === "+" ? -1 : 1;
    return utcTime + (tzHours * 60 + tzMinutes) * 60 * 1000 * direction;
};

/**
 * @description Parse dates in the default Skyward format, which is based on DateTime.ToString() in the en-US locale, as well as correctly formatted Datepicker
 * values.
 * @param {string} dateStr The Skyward date to be parsed.
 * @returns {Number} The date, as a number of milliseconds since Jan 01, 1970.
 */
export let parseServerDate = function (dateStr: string)
{
    // The DateTime format is based on CurrentCulture.ShortDatePattern and .LongTimePattern: https://docs.microsoft.com/en-us/dotnet/api/system.datetime.tostring?view=netframework-4.8#System_DateTime_ToString
    // For en-US these are "M/d/yyyy" and "h:mm:ss tt".  Note that this omits timezone info, so we assume local time.
    // The Datepicker format is "MM/dd/yyyy dddd".  We ignore the day-of-week portion.
    /*
    function optional(re)
    {
        return "(?:" + re + ")?";
    }
    function group(re)
    {
        return "(" + re + ")";
    }
    function choice(reArray)
    {
        return reArray.join("|");
    }
    // 1 or 2 month digits, 1 or 2 day digits, and 4 to 6 year digits
    var datePortion = "(\\d{1,2})/(\\d{1,2})/(\\d{4,6})"; // month: group 1, day: group 2, year: group 3
    // 1 or 2 hour digits, 2 minute digits, optionally 2 second digits, and optionally AM/PM (again, playing it safe)
    // Hours: group 4, minutes: group 5, seconds: group 6, am/pm: group 7
    var timePortion = "(\\d{1,2}):(\\d{2})" + optional(":(\\d{2})") + optional(" ([AP]M)");
    // Either a time, or 1 or more letters (hopefully the name of a day of the week)
    var timeOrDayOfWeek = group("?:" + choice([timePortion, "\\[a-z]+"]));
    // Wrap all the parts of the parser in ^ and $. ^ matches the start of the string, and $ matches the end, so this ensures there will be no leftover
    // leading or trailing characters.
    var skywardRegex = new RegExp("^" + datePortion + optional(" " + timeOrDayOfWeek) + "$", "i");
    */
    // This regex is produced by the above code
    const skywardRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4,6})(?: (?:(\d{1,2}):(\d{2})(?::(\d{2}))?(?: ([AP]M))?|\[a-z]+))?$/i;
    const match = skywardRegex.exec(dateStr);
    if (match === null)
    {
        return NaN;
    }
    const year = parseInt(match[3], 10);
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    let hours = parseInt(match[4] || "0", 10);
    const minutes = parseInt(match[5] || "0", 10);
    const seconds = parseInt(match[6] || "0", 10);
    const amOrPm = match[7];

    if (minutes >= 60 || seconds >= 60)
    {
        return NaN;
    }
    // 12-hour time
    if (amOrPm !== undefined)
    {
        // All browsers accept 0 AM and 0 PM.  Chrome, IE, and Edge accept 13:00 AM but reject 13:00 PM.  Firefox and Safari reject both.
        if (hours > 12)
        {
            return NaN;
        }
        hours = hours % 12 + (amOrPm.toLowerCase() === "am" ? 0 : 12);
    }
    // 24-hour time: allow 24:00:00 for consistency with parseISODate()
    else if (hours > 24 || hours === 24 && (minutes > 0 || seconds > 0))
    {
        return NaN;
    }
    if (!isValidYearMonthDay(year, month, day))
    {
        return NaN;
    }

    return newDate(year, month - 1, day, hours, minutes, seconds).getTime();
};

/**
 * @description Convert a value to a Date, using skyward.time.parseISODate() and parseServerDate() and optionally falling back to Date.parse().
 * @param {string|number|Date} dateValue The value to be converted to a Date.
 * @param {?boolean} useFallback Whether to fall back to Date.parse() if parsing fails.  Defaults to true.
 * @returns {Date} The parsed date.
 */
export let parseDate = function (dateValue: string | number | Date, useFallback?: boolean)
{
    if (typeof dateValue === "number")
    {
        return new Date(dateValue);
    }
    if (dateValue instanceof Date)
    {
        return dateValue;
    }
    // This happens relatively often with empty datepickers, and should not fall through to Date.parse
    if (dateValue === "" || dateValue === null || dateValue === undefined)
    {
        return new Date(NaN);
    }
    dateValue = dateValue.toString();
    var isoDate = parseISODate(dateValue);
    if (!isNaN(isoDate))
    {
        return new Date(isoDate);
    }
    var skywardDate = parseServerDate(dateValue);
    if (!isNaN(skywardDate))
    {
        return new Date(skywardDate);
    }
    if (useFallback)
    {
        console.warn("Non-standard date passed to parseDate()", dateValue);
        return new Date(Date.parse(dateValue));
    }
    return new Date(NaN);
};

/**
 * @description Check whether the given year, month, and day correspond to a real day in the Gregorian calendar.
 * @param {Number} year The year to test
 * @param {Number} month The month to test (1-based, unlike Javascript)
 * @param {Number} day The day to test
 * @returns {boolean} Whether the date is valid.
 */
export let isValidYearMonthDay = function (year: number, month: number, day: number)
{
    if (month < 1 || month > 12)
    {
        return false;
    }
    var monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || (day > monthLengths[month - 1] && !(month === 2 && day === 29 && (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)))))
    {
        return false;
    }
    return true;
};

export let now = function ()
{
    var durationSinceTimeUpdated = Date.now() - receivedTime!;
    return new Date(serverTime!.getTime() + durationSinceTimeUpdated);
};

export let setCurrentTime = function (dateTime: number)
{
    serverTime = new Date(dateTime);
    receivedTime = Date.now();
};

export let stopwatch = function ()
{
    var startTime = Date.now();
    var watch =
    {
        duration: function ()
        {
            return Date.now() - startTime;
        },
        stop: function ()
        {
            var endTime = Date.now();
            watch.duration = function ()
            {
                return endTime - startTime;
            };
        },
        restart: function ()
        {
            startTime = Date.now();
            watch.duration = function ()
            {
                return Date.now() - startTime;
            };
        }
    };
    return watch;
};

/**
 * Breaks total time into its whole parts and remainder per largestPeriod
 * @returns {[number, number]} First index is the whole part, second index is the reminder
 * @param {number} total Total time to break up
 * @param {number} largestPeriod The number of units in 1 period of the larger desired unit 
 * Notes: total and largestPeriod must be in the same units, aka milliseconds and milliseconds / seconds and seconds etc.
 */
export let breakTimeIntoPeriods = function(total: number, largestPeriod: number): [number, number]
{
    if (isNaN(total) || total <= 0)
    {
        return [0, 0];
    }

    var whole = Math.floor(total / largestPeriod);
    var remainder = Math.floor(total % largestPeriod);

    return [whole, remainder];
}

export let getDisplayTimespan = function (intervalInMilliseconds: number)
{
    if (isNaN(intervalInMilliseconds) || intervalInMilliseconds < 0)
    {
        return "0:00";
    }

    const milisecondsInAMinute = 1000 * 60;
    const [minutes, miliseconds] = breakTimeIntoPeriods(intervalInMilliseconds, milisecondsInAMinute);
    return minutes + ":" + (miliseconds / 1000).toString().padLeft(2, "0");
};

/**
 * Equivalent to the Date(year, month, day, ...) constructor, but correctly handling years between 0000 and 0099, rather than assuming they are 1900-1999.
 * @returns {Date} The constructed Date.
 * @param {number} year The year
 * @param {number} monthIndex The month, starting from 0 for January
 * @param {number} day The day of the month, starting from 1
 * @param {number?} hours Hour
 * @param {number?} minutes Minute
 * @param {number?} seconds Second
 * @param {number?} milliseconds Millisecond
 */
export let newDate = function (year: number, monthIndex: number, day: number = 1, hours: number = 0, minutes: number = 0, seconds: number = 0, milliseconds: number = 0)
{
    let date: Date;

    switch (arguments.length)
    {
        case 0: return new originalDateConstructor();
        case 1: return new originalDateConstructor(year);
        // Explicitly passing undefined for any argument should produce Invalid Date rather than the default value.
        default:
            date = new originalDateConstructor(year, monthIndex, day, hours, minutes, seconds, milliseconds);
    }
    // Manually set dates when needed.  Out-of-range values, like a monthIndex of -1, can cause the
    // date's year to vary from the `year` argument, so we can't blindly call date.setFullYear(year).
    if (year >= 0 && year <= 99)
    {
        date.setFullYear(date.getFullYear() - 1900);
    }
    return date;
};

/**
 * Format a date as unspecified-timezone ISO 8601: YYYY-MM-DDTHH:mm:SS.sss.  This is interpreted correctly on the server by Newtonsoft.Json and
 * DateTime.parse(), and on the client by skyward.time.parseISODate and Date.parse().
 * @param {Date} date The date to format
 * @param {boolean?} includeTime Whether to include the time of day.  Defaults to true.
 * @returns {string} The formatted date.
 */
export let formatServerDate = function (date: Date, includeTime: boolean = true)
{
    if (!date.isValidDate())
    {
        return "Invalid Date";
    }
    // Months are indexed from 0
    const month = (date.getMonth() + 1).toString().padLeft(2, "0");
    const day = date.getDate().toString().padLeft(2, "0");
    const year = date.getFullYear().toString().padLeft(4, "0");

    var timePart;
    if (includeTime)
    {
        var hours = date.getHours().toString().padLeft(2, "0");
        var minutes = date.getMinutes().toString().padLeft(2, "0");
        var seconds = date.getSeconds().toString().padLeft(2, "0");
        var milliseconds = date.getMilliseconds().toString().padLeft(3, "0").slice(0, 3);
        timePart = hours + ":" + minutes + ":" + seconds + "." + milliseconds;
    }
    else
    {
        // Newtonsoft requires the time in order to detect date strings.  Zeroing it out is the best we can do.
        timePart = "00:00:00.000";
    }


    return `${year}-${month}-${day}T${timePart}`;
};

/**
 * Check if a passed value is a Date object.
 * @param {any} value The value to check.
 * @param {boolean} allowInvalid True if Invalid Date objects should be treated as Dates.  Defaults to true.
 */
export let isDate = function (value: any, allowInvalid: boolean = true): value is Date
{
    const isDate = value && Object.prototype.toString.call(value) === "[object Date]";
    return isDate && (allowInvalid || !isNaN(value));
}

serverTime = parseDate($("#pageRenderTime").val() as string|number);
receivedTime = Date.now();
